import { ProductOnboardingView, ProductSpec, ResolvedOnboardingStep } from 'models'

/* The manifest owns each step's key, title, and whether it is required; the type
 * is the stable contract, so match on either. */
export const resolveSpecStep = (spec: ProductSpec | null, key: string): ResolvedOnboardingStep | undefined =>
  spec?.manifest?.config?.resolved_onboarding_steps?.find((step) => step.type === key || step.key === key)

/* The data steps read their Jira options through the connector these three set
 * up, so their catalogs stay unfetched until all three are completed. */
export const CONNECTOR_SETUP_STEP_KEYS = ['init', 'connector_domain', 'connected_projects']

export const isConnectorSetupCompleted = (view?: ProductOnboardingView | null) =>
  !!view && CONNECTOR_SETUP_STEP_KEYS.every((key) => view.steps?.find((s) => s.key === key)?.status === 'completed')

export const findStep = (view: ProductOnboardingView | null | undefined, key: string) =>
  view?.steps?.find((step) => step.key === key)

export const isStepCompleted = (view: ProductOnboardingView | null | undefined, key: string) =>
  findStep(view, key)?.status === 'completed'

/* A step is interactive once it is the server's current step, and stays visible
 * once completed; steps beyond it are not shown at all. */
export const isStepUnlocked = (view: ProductOnboardingView | null | undefined, key: string) =>
  !!view && (key === view.current_step || isStepCompleted(view, key))

export interface ContextVariant {
  label: string
  value: string
  description?: string
}

/* Locate the onyxia_context schema anywhere in the product spec. Its exact path
 * is backend-owned, so we search by key rather than assume a fixed location. */
const findOnyxiaContext = (node: any): any => {
  if (!node || typeof node !== 'object') return null
  if (node.onyxia_context && typeof node.onyxia_context === 'object') return node.onyxia_context
  for (const key of Object.keys(node)) {
    const found = findOnyxiaContext(node[key])
    if (found) return found
  }
  return null
}

/* Connector-domain options are spec-driven from onyxia_context.oneOf: each
 * variant's title is the label, its context_type const is the value, and its
 * description is the helper text. Nothing is hardcoded. */
export const resolveContextVariants = (spec: ProductSpec | null): ContextVariant[] => {
  if (!spec) return []
  const context = findOnyxiaContext(spec)
  const variants = context?.oneOf
  if (!Array.isArray(variants)) return []

  return variants
    .map((variant: any) => ({
      label: variant.title ?? '',
      value: variant.properties?.context_type?.const ?? variant.const ?? variant.context_type ?? '',
      description: variant.description
    }))
    .filter((variant: ContextVariant) => variant.value && variant.label)
}
